import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import useSiteContent from "@/lib/useSiteContent";
import { Sparkles, ArrowRight, Star } from "lucide-react";
import { MODULE_ICONS } from "./_shared";

export default function Hero() {
  const { text, image } = useSiteContent();
  const navigate = useNavigate();
  const background = image("image.hero.background", "/brand/hero-sunset.png");
  // The social-proof row: three editable portraits. The defaults are the stock photos the
  // page has always shown.
  const avatars = [
    { slot: "avatar1", ...image("image.hero.avatar1", "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop") },
    { slot: "avatar2", ...image("image.hero.avatar2", "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop") },
    { slot: "avatar3", ...image("image.hero.avatar3", "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop") },
  ];
  const modules = [
    { id: "practice", label: text("ecosystem.practice") },
    { id: "journeys", label: text("ecosystem.journeys") },
    { id: "ai", label: text("ecosystem.ai") },
    { id: "telehealth", label: text("ecosystem.telehealth") },
    { id: "automations", label: text("ecosystem.automations") },
    { id: "payments", label: text("ecosystem.payments") },
    { id: "insights", label: text("ecosystem.insights") },
    { id: "courses", label: text("ecosystem.courses") },
  ];
  return (
    <section className="relative pt-24 pb-0 overflow-hidden">
      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-10">
        <div
          className="relative rounded-[2.5rem] overflow-hidden min-h-[720px] lg:min-h-[820px] bg-gradient-to-br from-sol-ivory via-amber-50 to-orange-100"
          style={{
            backgroundImage: `linear-gradient(120deg, rgba(247,245,240,0.85) 0%, rgba(247,245,240,0.35) 50%, transparent 100%), url("${background.src}")`,
            backgroundSize: "cover",
            backgroundPosition: "center right",
          }}
        >
          <div className="relative z-10 px-8 lg:px-20 pt-20 pb-44 lg:pb-52">
            <div className="max-w-2xl">
              <h1 className="font-display font-medium text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] tracking-tight text-sol-midnight">
                {text("hero.titleA")}<br />{text("hero.titleB")}
              </h1>
              <div className="flex items-center gap-3 mt-8 mb-8">
                <div className="w-16 h-px bg-sol-gold" />
                <Sparkles className="w-4 h-4 text-sol-gold" />
                <div className="w-16 h-px bg-sol-gold" />
              </div>
              <p className="text-lg lg:text-xl text-sol-midnight/75 leading-relaxed max-w-xl">
                {text("hero.subtitle")}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="cta-hero-signup"
                  onClick={() => navigate("/platform/signup")}
                  className="bg-sol-gold hover:bg-sol-gold/90 text-sol-midnight rounded-2xl px-7 py-7 text-base font-semibold shadow-lg shadow-sol-gold/20"
                >
                  {text("hero.ctaPrimary")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={() => window.open("/demo", "_blank")}
                  variant="outline"
                  className="border-sol-midnight/15 bg-white/80 backdrop-blur-sm hover:bg-white text-sol-midnight rounded-2xl px-7 py-7 text-base font-semibold"
                  data-testid="cta-hero-demo"
                >
                  {text("hero.ctaSecondary")}
                </Button>
              </div>
              {/* Social proof */}
              <div className="mt-14 flex items-center gap-4">
                <div className="flex -space-x-3">
                  {avatars.map((avatar) => (
                    <img key={avatar.slot} src={avatar.src} alt={avatar.alt} className="w-10 h-10 rounded-full border-2 border-sol-ivory object-cover" />
                  ))}
                </div>
                <div>
                  <div className="flex gap-0.5 mb-1">
                    {[1,2,3,4,5].map((star) => <Star key={`star-${star}`} className="w-3.5 h-3.5 text-sol-gold fill-sol-gold" />)}
                  </div>
                  <div className="text-xs text-sol-midnight/70">{text("hero.trustedBy")}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Module bar (glassmorphic) */}
          <div className="absolute bottom-0 left-0 right-0 z-10">
            <div
              className="mx-4 lg:mx-8 mb-4 lg:mb-8 rounded-2xl px-4 lg:px-8 py-4 lg:py-6 grid grid-cols-3 lg:grid-cols-9 gap-2 lg:gap-3 backdrop-blur-xl"
              style={{ background: "rgba(11, 16, 32, 0.78)" }}
            >
              {modules.map((mod) => {
                const Icon = MODULE_ICONS[mod.id];
                return (
                  <div
                    key={`eco-${mod.id}`}
                    data-testid={`module-ecosystem-${mod.id}`}
                    className="flex flex-col items-center gap-2 text-center px-1 lg:px-2 py-2 rounded-xl hover:bg-white/5 transition cursor-pointer"
                  >
                    <Icon className="w-6 h-6 lg:w-7 lg:h-7 text-sol-gold" strokeWidth={1.4} />
                    <span className="text-[10px] lg:text-xs leading-tight text-sol-ivory/85 font-medium">
                      {mod.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
